import React, { useCallback, useState } from 'react';
import { Dropdown, DropdownItem } from '../../dropdown';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ButtonDiv, IconDiv, IconFrame, InviteButton, MainDiv, PlanDiv, SubView, Text, TitleDiv, View } from './styles';
import { isEmpty } from '../../../helpers/common';
import { getCookie, setCookie } from '../../../helpers/cookie';
import { UpdateDataInterface } from '../../../interfaces/navbarInterface';
import update from '../../../update.json';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { useLocation } from 'react-router-dom';
import {
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  HIDE_INVITE_TEAMMATE_BUTTON,
  ICON_SIZE,
  FREE_PLAN_TASK_LIMIT,
  INVITE_TEAMMATE_MAX_USERS,
  INVITE_DISMISS_EXPIRY_DAYS
} from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import Modal from '../../modals/modal';
import InviteTeamsModal from '../../modals/inviteTeamsModal';
import IconButton from '../../button/iconButton';
import { observer } from 'mobx-react-lite';
import Image from '../../image';

interface HelpSupportDropdownProps {
  setOpenContactUsModal?: () => void;
  isMobile?: boolean;
  isSetting?: boolean;
  onClickOpenModel?: () => void;
}

const HelpSupportDropdown: React.FC<HelpSupportDropdownProps> = ({
  setOpenContactUsModal,
  isMobile,
  isSetting,
  onClickOpenModel
}) => {
  //State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inviteMember, setInviteMember] = useState<boolean>(true);

  //mobx store variables
  const { workspaceStore, settingStore } = rootStore;
  const { workspaceTotalTask, currentWorkspace } = workspaceStore;
  const { subscriptionList } = settingStore;

  //other variables
  const location = useLocation();

  const isShowInviteTeamMembers = useCallback(() => {
    const cookieValue = getCookie(`${HIDE_INVITE_TEAMMATE_BUTTON}-${currentWorkspace?.id}`);
    return cookieValue !== 'true';
  }, [currentWorkspace?.id]);

  const getPathName = useCallback(() => {
    type PathMap = {
      [key: string]: string;
    };

    const source: PathMap = {
      '/my-tasks': 'mytask',
      '/invoices': 'invoice',
      '/timeTracking': 'time tracking',
      '/report': 'report',
      '/projects': 'project',
      '/messages': 'messages',
      '/customer': 'customer',
      '/settings': 'settings',
      '/': 'inbox'
    };
    const matchedKey = (Object.keys(source) as Array<keyof typeof source>).find((key) =>
      location.pathname.startsWith(key.toString())
    );
    return matchedKey ? source[matchedKey] : 'unknown';
  }, [location.pathname]);

  const trackSupportActivity = useCallback(
    (event: string) => {
      const path = getPathName();
      trackAnalyticActivity(`support: ${event}`, { source: `${path}` });
    },
    [getPathName]
  );

  const handleMenuItemClick = (url: string, event?: string) => {
    if (event) trackSupportActivity(event);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onContactUsClick = useCallback(() => {
    if (window.Intercom && typeof window.Intercom === 'function') {
      window.Intercom('show');
    } // if (setOpenContactUsModal) setOpenContactUsModal();
    trackSupportActivity('contact us clicked');
  }, [trackSupportActivity]);

  const isFreePlanButtonVisible =
    subscriptionList &&
    subscriptionList?.activeSubscription?.length === 0 &&
    workspaceTotalTask >= FREE_PLAN_TASK_LIMIT;

  const handleOnClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDismissInvite = useCallback(() => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + INVITE_DISMISS_EXPIRY_DAYS);
    setCookie(`${HIDE_INVITE_TEAMMATE_BUTTON}-${currentWorkspace?.id}`, 'true', {
      expires: expirationDate,
      path: '/'
    });
    setInviteMember(false);
  }, [currentWorkspace?.id]);

  return (
    <MainDiv isFreePlanButtonVisible={!!isFreePlanButtonVisible} className='help-support-dropdown'>
      {currentWorkspace?.users &&
        currentWorkspace.users.length < INVITE_TEAMMATE_MAX_USERS &&
        isShowInviteTeamMembers() &&
        inviteMember &&
        (currentWorkspace.isOwner || currentWorkspace.isAdmin) && (
          <InviteButton isFreePlanButtonVisible={!!isFreePlanButtonVisible}>
            <TitleDiv role='button' onClick={handleOnClick}>
              <SVGIcon
                name='mail-icon'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              />
              <p>Invite teammates</p>
            </TitleDiv>
            <IconDiv role='button' className='icon-div' onClick={handleDismissInvite}>
              <IconFrame>
                <IconButton
                  iconName='new-close-icon'
                  size={ICON_BUTTON_SIZE.EXTRA_X_SMALL}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                />
              </IconFrame>
            </IconDiv>
          </InviteButton>
        )}
      <PlanDiv isFreePlanButtonVisible={!!isFreePlanButtonVisible}>
        <Dropdown
          isMinWidth={210}
          modalTitle='Help & Support'
          content={
            isMobile ? (
              <ButtonDiv role='button' onClick={() => trackSupportActivity('opened')}>
                <DropdownItem
                  label='Help & Support'
                  iconName={'help-support-icon'}
                  iconSize={isSetting ? 20 : 16}
                  iconViewBox='0 0 18 18'
                  iconTone='fill'
                />
              </ButtonDiv>
            ) : (
              <IconButton
                iconName='question-mark-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.SECONDARY}
                iconColorType='primary'
              />
            )
          }
          trigger='click'>
          <DropdownItem
            key={1}
            label='Help Center'
            iconName='help-center-icon'
            iconSize={16}
            iconViewBox='0 0 18 18'
            iconTone='fill'
            onClick={() => handleMenuItemClick('https://www.teamcamp.app/help', 'help center clicked')}
            isSuffix={
              <SVGIcon
                name='arrow-ne'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
              />
            }
          />
          <DropdownItem
            key={2}
            label='API Docs'
            iconName='html-code-icon'
            iconSize={18}
            iconTone='fill'
            onClick={() =>
              handleMenuItemClick('https://api.teamcamp.app/api-reference/introduction', 'api docs clicked')
            }
            isSuffix={
              <SVGIcon
                name='arrow-ne'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
              />
            }
          />
          <DropdownItem
            key={3}
            label='Contact us'
            iconName='message-icon'
            iconSize={16}
            iconViewBox='0 0 18 18'
            onClick={onContactUsClick}
            iconTone='fill'
          />
          <Text>What's new</Text>
          {update?.length > 0 &&
            update?.map(
              (item: UpdateDataInterface) =>
                !isEmpty(item.title) && (
                  <DropdownItem key={item?.redirect_url} onClick={() => handleMenuItemClick(item?.redirect_url)}>
                    <View>
                      <SubView>
                        <Image src={item?.icon_url} alt={item?.title} width='18' height='18' className='stroke-color' />
                        <p>{item?.title}</p>
                      </SubView>
                    </View>
                  </DropdownItem>
                )
            )}
          <DropdownItem
            key={6}
            label='Full Changelog'
            iconName='calendar-icon'
            iconSize={16}
            iconTone='fill'
            iconViewBox='0 0 18 18'
            onClick={() => handleMenuItemClick('https://www.teamcamp.app/updates', 'full changelog clicked')}
            isSuffix={
              <SVGIcon
                name='arrow-ne'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-primary)'
              />
            }
          />
        </Dropdown>
      </PlanDiv>
      <Modal open={isModalOpen} onClose={handleCloseModal} maxWidth={'400px'}>
        <InviteTeamsModal onCancel={handleCloseModal} workspace_id={currentWorkspace?.id || ''} />
      </Modal>
    </MainDiv>
  );
};

export default observer(HelpSupportDropdown);
